import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Upload, FileText, CheckCircle, AlertTriangle, Loader2,
  ChevronDown, Phone, Mail, MapPin, Users
} from 'lucide-react';
import { parseVCardFile, mapContactToPreview, normalizePhone, detectCategory } from '../../utils/vcard_parser';
import { supplierAPI } from '../../api';

// Category codes — same set as FournisseursView. Labels resolved at render via
// t('suppliers.categories.<code>') (module constants are evaluated once, before
// any language change; see CLAUDE.md i18n traps).
const CATEGORY_CODES = [
  'insurer', 'broker', 'garage', 'tire', 'fuel', 'leasing', 'glass', 'bodywork',
  'towing', 'parking', 'toll', 'washing', 'inspection', 'equipment', 'parts',
  'rental', 'expert', 'legal', 'dealer', 'other',
];

/**
 * VCard Import Modal — 3-step flow: Drop → Preview → Summary
 */
export default function VCardImportModal({ onClose, onSuccess, existingSuppliers = [] }) {
  const { t } = useTranslation();
  const categoryOptions = useMemo(
    () => CATEGORY_CODES.map((value) => ({ value, label: t(`suppliers.categories.${value}`) })),
    [t]
  );
  const [step, setStep] = useState(1); // 1=drop, 2=preview, 3=summary
  const [file, setFile] = useState(null);
  const [contacts, setContacts] = useState([]); // parsed preview rows
  const [selected, setSelected] = useState([]); // boolean[] per row
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Build doublon lookup from existing suppliers
  const existingPhones = useRef(new Set());
  const existingNames = useRef(new Set());

  if (existingPhones.current.size === 0 && existingSuppliers.length > 0) {
    for (const s of existingSuppliers) {
      if (s.phone) existingPhones.current.add(normalizePhone(s.phone));
      if (s.mobile) existingPhones.current.add(normalizePhone(s.mobile));
      existingNames.current.add((s.name || '').toLowerCase().trim());
    }
  }

  const checkDoublon = useCallback((preview) => {
    const phones = [preview.phone, preview.mobile].filter(Boolean).map(normalizePhone);
    const isPhoneMatch = phones.some(p => existingPhones.current.has(p));
    const isNameMatch = existingNames.current.has((preview.name || '').toLowerCase().trim());
    return isPhoneMatch || (isNameMatch && phones.length === 0);
  }, []);

  // Handle file selection (drop or picker)
  const processFile = useCallback((f) => {
    if (!f) return;
    setFile(f);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseVCardFile(text);
      const previews = parsed.map(c => {
        const preview = mapContactToPreview(c);
        preview.isDoublon = checkDoublon(preview);
        return preview;
      });
      setContacts(previews);
      // Select non-doublons by default
      setSelected(previews.map(p => !p.isDoublon));
      setStep(2);
    };
    reader.readAsText(f);
  }, [checkDoublon]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && (f.name.endsWith('.vcf') || f.type === 'text/vcard' || f.type === 'text/x-vcard')) {
      processFile(f);
    }
  }, [processFile]);

  const handleFileInput = useCallback((e) => {
    processFile(e.target.files[0]);
  }, [processFile]);

  const toggleSelect = (idx) => {
    setSelected(prev => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  const toggleAll = () => {
    const allChecked = selected.every(Boolean);
    setSelected(selected.map(() => !allChecked));
  };

  const updateCategory = (idx, newCat) => {
    setContacts(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], category: newCat };
      return next;
    });
  };

  // Import selected contacts
  const handleImport = async () => {
    setImporting(true);
    try {
      const selectedIndexes = selected
        .map((checked, i) => checked ? i : null)
        .filter(i => i !== null);

      const res = await supplierAPI.importVCards(file, selectedIndexes);
      setResults(res.data);
      setStep(3);
      if (res.data?.imported > 0) {
        onSuccess?.();
      }
    } catch (err) {
      setResults({ total_parsed: contacts.length, imported: 0, skipped_doublons: 0, errors: 1, details: [{ status: 'error', error: err.message }] });
      setStep(3);
    } finally {
      setImporting(false);
    }
  };

  const selectedCount = selected.filter(Boolean).length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-amber-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('suppliers.vcard.title')}</h2>
              <p className="text-sm text-gray-500">
                {step === 1 && t('suppliers.vcard.stepDrop')}
                {step === 2 && t('suppliers.vcard.stepPreview', { count: contacts.length, selected: selectedCount })}
                {step === 3 && t('suppliers.vcard.stepDone')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">
          {/* Step 1: Drop Zone */}
          {step === 1 && (
            <div
              className={`border-2 border-dashed rounded-xl p-12 text-center transition cursor-pointer ${
                dragOver ? 'border-amber-500 bg-amber-50' : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50/50'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                {t('suppliers.vcard.dropTitle')}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                {t('suppliers.vcard.dropSubtitle')}
              </p>
              <p className="text-xs text-gray-400">
                {t('suppliers.vcard.dropFormats')}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".vcf,text/vcard,text/x-vcard"
                className="hidden"
                onChange={handleFileInput}
              />
            </div>
          )}

          {/* Step 2: Preview Table */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Info banner for doublons */}
              {contacts.some(c => c.isDoublon) && (
                <div className="flex items-center gap-2 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {t('suppliers.vcard.doublonBanner')}
                </div>
              )}

              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left w-10">
                        <input
                          type="checkbox"
                          checked={selectedCount === contacts.length}
                          onChange={toggleAll}
                          className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        />
                      </th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">{t('suppliers.vcard.table.name')}</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600 w-48">{t('suppliers.vcard.table.category')}</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">{t('suppliers.vcard.table.phone')}</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">{t('suppliers.vcard.table.email')}</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">{t('suppliers.vcard.table.city')}</th>
                      <th className="px-3 py-2 text-center font-semibold text-gray-600 w-20">{t('suppliers.vcard.table.doublon')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {contacts.map((c, i) => (
                      <tr
                        key={i}
                        className={`${c.isDoublon ? 'bg-yellow-50' : 'hover:bg-gray-50'} transition`}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selected[i]}
                            onChange={() => toggleSelect(i)}
                            className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{c.name}</div>
                          {c.org && c.org !== c.name && (
                            <div className="text-xs text-gray-500">{c.org}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="relative">
                            <select
                              value={c.category}
                              onChange={(e) => updateCategory(i, e.target.value)}
                              className="appearance-none w-full pl-2 pr-7 py-1 text-xs border border-gray-200 rounded bg-white focus:ring-1 focus:ring-amber-500"
                            >
                              {categoryOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          {c.phone && (
                            <div className="flex items-center gap-1 text-gray-600">
                              <Phone className="w-3 h-3" />
                              <span className="truncate max-w-28">{c.phone}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {c.email && (
                            <div className="flex items-center gap-1 text-gray-600">
                              <Mail className="w-3 h-3" />
                              <span className="truncate max-w-36">{c.email}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {c.city && (
                            <div className="flex items-center gap-1 text-gray-600">
                              <MapPin className="w-3 h-3" />
                              {c.city}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {c.isDoublon && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                              <AlertTriangle className="w-3 h-3" />
                              {t('suppliers.vcard.doublonYes')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Step 3: Summary */}
          {step === 3 && results && (
            <div className="space-y-6">
              {/* Stats cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-green-700">{results.imported}</div>
                  <div className="text-sm text-green-600">{t('suppliers.vcard.imported')}</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <AlertTriangle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-yellow-700">{results.skipped_doublons}</div>
                  <div className="text-sm text-yellow-600">{t('suppliers.vcard.doublonsSkipped')}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <X className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-red-700">{results.errors}</div>
                  <div className="text-sm text-red-600">{t('suppliers.vcard.errors')}</div>
                </div>
              </div>

              {/* Error details */}
              {results.details?.some(d => d.status === 'error') && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-800 mb-2">{t('suppliers.vcard.errorDetails')}</h4>
                  <ul className="space-y-1 text-sm text-red-700">
                    {results.details.filter(d => d.status === 'error').map((d, i) => (
                      <li key={i}>{d.name || t('suppliers.vcard.contactFallback')}: {d.error || t('suppliers.vcard.unknownError')}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          {step === 2 && (
            <button
              onClick={() => { setStep(1); setFile(null); setContacts([]); }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
            >
              {t('suppliers.vcard.back')}
            </button>
          )}
          {step !== 2 && <div />}

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-100"
            >
              {step === 3 ? t('common.actions.close') : t('common.actions.cancel')}
            </button>

            {step === 2 && (
              <button
                onClick={handleImport}
                disabled={importing || selectedCount === 0}
                className="flex items-center gap-2 px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('suppliers.vcard.importing')}
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    {t('suppliers.vcard.import', { count: selectedCount })}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
